// ============================================================================
// periodComparison-renderer.js
//
// Renders the period-over-period comparison JSON (from periodComparison-prompt.js)
// into a self-contained HTML string for display in the Process Health tab.
//
// Design language matches executiveAssessment-renderer.js:
//   - Atlassian colour palette (#0052CC, #FF8B00, #DE350B, #00875A)
//   - Card-based layout, consistent typography
//   - No external dependencies — pure HTML/CSS string
// ============================================================================

const esc = (v = '') =>
  String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// ── Verdict config ─────────────────────────────────────────────────────────────

const VERDICT_CONFIG = {
  'Significantly Improved':    { bg: '#E3FCEF', border: '#00875A', text: '#006644', dot: '#00875A', icon: '▲▲' },
  'Improved':                  { bg: '#E3FCEF', border: '#57D9A3', text: '#006644', dot: '#00875A', icon: '▲'  },
  'Stable':                    { bg: '#F4F5F7', border: '#97A0AF', text: '#42526E', dot: '#6B778C', icon: '→'  },
  'Deteriorated':              { bg: '#FFEBE6', border: '#FF8F73', text: '#BF2600', dot: '#DE350B', icon: '▼'  },
  'Significantly Deteriorated':{ bg: '#FFEBE6', border: '#DE350B', text: '#BF2600', dot: '#BF2600', icon: '▼▼' },
};

const getVerdictConfig = (verdict) =>
  VERDICT_CONFIG[verdict] || VERDICT_CONFIG['Stable'];

// ── Direction config ───────────────────────────────────────────────────────────

const DIRECTION_CONFIG = {
  improved:    { bg: '#E3FCEF', text: '#006644', border: '#57D9A3', arrow: '▲', label: 'Improved'    },
  degraded:    { bg: '#FFEBE6', text: '#BF2600', border: '#FF8F73', arrow: '▼', label: 'Degraded'    },
  unchanged:   { bg: '#F4F5F7', text: '#42526E', border: '#DFE1E6', arrow: '→', label: 'Unchanged'   },
  unavailable: { bg: '#F4F5F7', text: '#6B778C', border: '#DFE1E6', arrow: '–', label: 'No data'     },
};

const getDirConfig = (direction) =>
  DIRECTION_CONFIG[direction?.toLowerCase()] || DIRECTION_CONFIG['unavailable'];

// ── Significance badge ─────────────────────────────────────────────────────────

const significanceBadge = (sig) => {
  if (sig === 'high')   return `<span style="display:inline-flex;align-items:center;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700;background:#FFEBE6;color:#BF2600;border:1px solid #FF8F73;">High impact</span>`;
  if (sig === 'medium') return `<span style="display:inline-flex;align-items:center;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700;background:#FFF0B3;color:#974F0C;border:1px solid #FFE380;">Medium</span>`;
  return `<span style="display:inline-flex;align-items:center;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700;background:#F4F5F7;color:#42526E;border:1px solid #DFE1E6;">Low</span>`;
};

// ── Confidence badge ───────────────────────────────────────────────────────────

const confidenceBadge = (conf) => {
  const map = {
    High:     { bg: '#E3FCEF', text: '#006644', border: '#57D9A3' },
    Moderate: { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380' },
    Low:      { bg: '#F4F5F7', text: '#42526E', border: '#DFE1E6' },
  };
  const c = map[conf] || map.Low;
  return `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${c.bg};color:${c.text};border:1px solid ${c.border};">✓ Confidence: ${esc(conf)}</span>`;
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = `
  html,body{margin:0;padding:0;box-sizing:border-box;}
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif;background:#F4F5F7;color:#172B4D;padding:0;}
  .pc-wrap{display:flex;flex-direction:column;gap:0;padding:10px;}
  .pc-doc-header{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:14px 20px;margin-bottom:10px;display:flex;justify-content:space-between;align-items:center;}
  .pc-doc-header-left{display:flex;flex-direction:column;gap:4px;}
  .pc-doc-header-title{font-size:16px;font-weight:700;color:#172B4D;}
  .pc-doc-header-type{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#0052CC;}
  .pc-doc-header-time{font-size:12px;color:#6B778C;}
  .pc-section{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:18px 20px;margin-bottom:10px;}
  .pc-hero{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:20px 22px;position:relative;overflow:hidden;margin-bottom:10px;}
  .pc-hero::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#0052CC 0%,#00875A 50%,#FF8B00 100%);}
  .pc-eyebrow{font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#0052CC;margin-bottom:10px;display:flex;align-items:center;gap:6px;}
  .pc-eyebrow::before{content:'';display:inline-block;width:16px;height:2px;background:#0052CC;border-radius:2px;}
  .pc-headline{font-size:22px;font-weight:800;line-height:1.25;color:#172B4D;margin-bottom:12px;}
  .pc-leadership{font-size:14px;line-height:1.7;color:#42526E;padding-bottom:16px;border-bottom:1px solid #EBECF0;margin-bottom:14px;}
  .pc-period-pills{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px;}
  .pc-period-pill{display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:999px;font-size:12px;font-weight:700;}
  .pc-section-header{display:flex;align-items:center;gap:10px;margin-bottom:16px;}
  .pc-section-icon{width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;font-size:15px;}
  .pc-section-title{font-size:17px;font-weight:700;color:#172B4D;}
  .pc-metric-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;}
  .pc-metric-card{border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:14px 16px;display:flex;flex-direction:column;gap:6px;}
  .pc-metric-top{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;}
  .pc-metric-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;}
  .pc-metric-values{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;}
  .pc-metric-baseline{font-size:13px;color:#6B778C;text-decoration:line-through;}
  .pc-metric-current{font-size:22px;font-weight:800;color:#172B4D;line-height:1.1;}
  .pc-metric-delta{font-size:12px;font-weight:700;padding:2px 8px;border-radius:999px;}
  .pc-metric-why{font-size:12px;line-height:1.55;color:#42526E;}
  .pc-signal-list{display:flex;flex-direction:column;gap:8px;}
  .pc-signal-card{border-radius:10px;padding:14px 16px;border:1px solid;}
  .pc-signal-title{font-size:14px;font-weight:700;margin-bottom:4px;}
  .pc-signal-meta{display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-bottom:6px;}
  .pc-signal-why{font-size:13px;line-height:1.6;}
  .pc-transition-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .pc-transition-card{border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:12px 14px;}
  .pc-transition-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:6px;}
  .pc-transition-row{font-size:12px;color:#172B4D;padding:5px 0;border-bottom:1px solid #EBECF0;display:flex;justify-content:space-between;gap:8px;}
  .pc-transition-row:last-child{border-bottom:none;}
  .pc-hypothesis-card{border:1px solid #EBECF0;border-radius:10px;background:#FAFBFC;padding:14px 16px;margin-bottom:8px;}
  .pc-hypothesis-card:last-child{margin-bottom:0;}
  .pc-hypothesis-title{font-size:14px;font-weight:700;color:#172B4D;margin-bottom:6px;}
  .pc-hypothesis-text{font-size:13px;line-height:1.65;color:#42526E;margin-bottom:8px;}
  .pc-hypothesis-evidence{font-size:12px;color:#42526E;padding:8px 10px;background:#F4F5F7;border-radius:6px;border-left:3px solid #DFE1E6;}
  .pc-chip{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;border:1px solid #DFE1E6;background:#fff;color:#42526E;}
  .pc-variant-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px;}
  .pc-variant-stat{background:#FAFBFC;border:1px solid #EBECF0;border-radius:10px;padding:12px 14px;}
  .pc-variant-stat-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:4px;}
  .pc-variant-stat-val{font-size:22px;font-weight:800;color:#172B4D;}
  .pc-variant-stat-sub{font-size:11px;color:#6B778C;margin-top:2px;}
  .pc-note{background:linear-gradient(135deg,#F0F7FF 0%,#E6F1FB 100%);border:1px solid #B3D4FF;border-radius:10px;padding:16px 18px;display:flex;gap:12px;align-items:flex-start;}
  .pc-note-icon{width:32px;height:32px;border-radius:8px;background:#0052CC;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;font-size:15px;}
  .pc-note-text{font-size:13px;line-height:1.7;color:#0747A6;}
  @media print{body{background:#fff;}.pc-section,.pc-hero{break-inside:avoid;}}
`;

// ── Section builders ───────────────────────────────────────────────────────────

const buildDocHeader = (modelName) => {
  const timestamp = new Date().toLocaleString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric', 
    hour: '2-digit', minute: '2-digit', hour12: true 
  });
  return `
<div class="pc-doc-header">
  <div class="pc-doc-header-left">
    <div class="pc-doc-header-type">Trend Analysis</div>
    <div class="pc-doc-header-title">${esc(modelName || 'Process Model')}</div>
  </div>
  <div class="pc-doc-header-time">${timestamp}</div>
</div>`;
};

const buildHero = (data) => {
  const summary   = data.comparison_summary || {};
  const verdict   = data.overall_verdict    || {};
  const vc        = getVerdictConfig(verdict.verdict);
  const cc        = getDirConfig(verdict.verdict?.toLowerCase().includes('improved') ? 'improved' : verdict.verdict?.toLowerCase().includes('deterior') ? 'degraded' : 'unchanged');
  const labelA    = esc(summary.period_baseline_label   || 'Baseline');
  const labelB    = esc(summary.period_comparison_label || 'Comparison');

  return `
<div class="pc-hero">
  <div class="pc-eyebrow">Trend Analysis</div>
  <div class="pc-headline">${esc(summary.headline || 'Period comparison complete.')}</div>
  <div class="pc-leadership">${esc(summary.leadership_summary || '')}</div>
  <div class="pc-period-pills">
    <span class="pc-period-pill" style="background:#E6F1FB;color:#0052CC;border:1px solid #85B7EB;">📅 Baseline: ${labelA}</span>
    <span class="pc-period-pill" style="background:#EAE6FF;color:#0747A6;border:1px solid #998DD9;">📅 Comparison: ${labelB}</span>
  </div>
  <div style="border-radius:10px;overflow:hidden;border:1px solid ${vc.border};background:${vc.bg};">
    <div style="display:flex;align-items:center;gap:16px;padding:16px 20px;">
      <div style="width:56px;height:56px;border-radius:50%;background:${vc.border};display:flex;align-items:center;justify-content:center;font-size:22px;color:#fff;font-weight:800;flex-shrink:0;">${vc.icon}</div>
      <div style="flex:1;">
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;color:${vc.text};opacity:.8;margin-bottom:4px;">Overall verdict</div>
        <div style="font-size:24px;font-weight:800;color:${vc.text};line-height:1.1;margin-bottom:6px;">${esc(verdict.verdict || 'Stable')}</div>
        ${confidenceBadge(verdict.confidence || 'Moderate')}
      </div>
    </div>
    <div style="font-size:13px;line-height:1.65;color:${vc.text};padding:12px 20px;border-top:1px solid ${vc.border}20;">${esc(verdict.verdict_rationale || '')}</div>
  </div>
</div>`;
};

const buildMetricDeltas = (data) => {
  const deltas = data.metric_deltas || [];
  if (deltas.length === 0) return '';

  const cards = deltas.map((m) => {
    const dc = getDirConfig(m.direction);
    return `
    <div class="pc-metric-card">
      <div class="pc-metric-top">
        <div class="pc-metric-label">${esc(m.metric)}</div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
          <span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:999px;font-size:11px;font-weight:700;background:${dc.bg};color:${dc.text};border:1px solid ${dc.border};">${dc.arrow} ${dc.label}</span>
          ${significanceBadge(m.significance)}
        </div>
      </div>
      <div class="pc-metric-values">
        <span class="pc-metric-baseline">${esc(m.baseline_value)}</span>
        <span style="font-size:13px;color:#6B778C;">→</span>
        <span class="pc-metric-current" style="color:${m.direction === 'improved' ? '#006644' : m.direction === 'degraded' ? '#BF2600' : '#172B4D'};">${esc(m.comparison_value)}</span>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">
        <span class="pc-metric-delta" style="background:${dc.bg};color:${dc.text};">${esc(m.absolute_delta || '')} (${esc(m.percent_change || '')})</span>
      </div>
      <div class="pc-metric-why">${esc(m.why_it_matters)}</div>
    </div>`;
  }).join('');

  return `
<div class="pc-section">
  <div class="pc-section-header">
    <div class="pc-section-icon" style="background:#0052CC;">📊</div>
    <div class="pc-section-title">Metric-by-Metric Comparison</div>
  </div>
  <div class="pc-metric-grid">${cards}</div>
</div>`;
};

const buildSignalBlock = (signals, title, scheme, icon) => {
  if (!signals || signals.length === 0) return '';
  const cards = signals.map((s) => `
    <div class="pc-signal-card" style="background:${scheme.bg};border-color:${scheme.border};">
      <div class="pc-signal-title" style="color:${scheme.text};">${esc(s.title)}</div>
      <div class="pc-signal-meta">
        <span class="pc-chip" style="background:${scheme.bg};border-color:${scheme.border};color:${scheme.text};">${esc(s.metric_or_transition)}</span>
        <span style="font-size:12px;color:${scheme.text};font-weight:700;">${esc(s.baseline_value)} → ${esc(s.comparison_value)}</span>
        <span style="font-size:12px;font-weight:800;color:${scheme.text};">${esc(s.delta)}</span>
      </div>
      <div class="pc-signal-why" style="color:${scheme.text};">${esc(s.why_it_matters)}</div>
    </div>`).join('');

  return `
<div class="pc-section">
  <div class="pc-section-header">
    <div class="pc-section-icon" style="background:${scheme.iconBg};">${icon}</div>
    <div class="pc-section-title">${title}</div>
  </div>
  <div class="pc-signal-list">${cards}</div>
</div>`;
};

const buildTransitionChanges = (data) => {
  const tc = data.transition_changes || {};
  const improvements  = tc.notable_improvements  || [];
  const deteriorations = tc.notable_deteriorations || [];
  if (improvements.length === 0 && deteriorations.length === 0) return '';

  const buildRows = (rows, dirColor) =>
    rows.map((r) => `
      <div class="pc-transition-row">
        <span style="flex:1;font-weight:600;">${esc(r.transition)}</span>
        <span style="white-space:nowrap;color:#6B778C;">${esc(r.baseline_avg_hrs)} → ${esc(r.comparison_avg_hrs)} hrs</span>
        <span style="font-weight:700;color:${dirColor};white-space:nowrap;">${esc(r.delta_hrs)}</span>
      </div>`).join('') || '<div class="pc-transition-row" style="color:#6B778C;">None identified</div>';

  const appearedList   = (tc.appeared_in_comparison    || []).map((t) => `<span class="pc-chip" style="background:#E3FCEF;color:#006644;border-color:#57D9A3;">${esc(t)}</span>`).join('');
  const disappearedList = (tc.disappeared_in_comparison || []).map((t) => `<span class="pc-chip" style="background:#FFF0B3;color:#974F0C;border-color:#FFE380;">${esc(t)}</span>`).join('');

  return `
<div class="pc-section">
  <div class="pc-section-header">
    <div class="pc-section-icon" style="background:#0747A6;">⇄</div>
    <div class="pc-section-title">Transition Changes</div>
  </div>
  <div class="pc-transition-grid">
    <div class="pc-transition-card">
      <div class="pc-transition-label">⬆ Improved transitions</div>
      ${buildRows(improvements, '#006644')}
    </div>
    <div class="pc-transition-card">
      <div class="pc-transition-label">⬇ Deteriorated transitions</div>
      ${buildRows(deteriorations, '#BF2600')}
    </div>
  </div>
  ${appearedList || disappearedList ? `
  <div style="margin-top:12px;display:flex;flex-direction:column;gap:8px;">
    ${appearedList   ? `<div><div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:6px;">New in comparison period</div><div style="display:flex;flex-wrap:wrap;gap:6px;">${appearedList}</div></div>` : ''}
    ${disappearedList ? `<div><div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:6px;">Absent in comparison period</div><div style="display:flex;flex-wrap:wrap;gap:6px;">${disappearedList}</div></div>` : ''}
  </div>` : ''}
  ${tc.rework_volume_change ? `<div style="margin-top:12px;font-size:13px;line-height:1.65;color:#42526E;padding:10px 12px;background:#F4F5F7;border-radius:8px;"><strong>Rework volume shift:</strong> ${esc(tc.rework_volume_change)}</div>` : ''}
</div>`;
};

const buildVariantChanges = (data) => {
  const vc = data.variant_changes || {};
  if (!vc.variant_count_baseline && !vc.variant_count_comparison) return '';

  const countDelta = (vc.variant_count_comparison || 0) - (vc.variant_count_baseline || 0);
  const countDir   = countDelta < 0 ? 'improved' : countDelta > 0 ? 'degraded' : 'unchanged';
  const dc         = getDirConfig(countDir);

  return `
<div class="pc-section">
  <div class="pc-section-header">
    <div class="pc-section-icon" style="background:#FF8B00;">⋯</div>
    <div class="pc-section-title">Workflow Path Changes</div>
  </div>
  <div class="pc-variant-grid">
    <div class="pc-variant-stat">
      <div class="pc-variant-stat-label">Variants — baseline</div>
      <div class="pc-variant-stat-val">${esc(String(vc.variant_count_baseline ?? '—'))}</div>
      <div class="pc-variant-stat-sub">Rework share: ${esc(vc.rework_variant_share_baseline || '—')}</div>
    </div>
    <div class="pc-variant-stat" style="border-color:${dc.border};background:${dc.bg};">
      <div class="pc-variant-stat-label" style="color:${dc.text};">Variants — comparison</div>
      <div class="pc-variant-stat-val" style="color:${dc.text};">${esc(String(vc.variant_count_comparison ?? '—'))} <span style="font-size:14px;font-weight:700;">${dc.arrow} ${countDelta > 0 ? '+' : ''}${countDelta}</span></div>
      <div class="pc-variant-stat-sub" style="color:${dc.text};">Rework share: ${esc(vc.rework_variant_share_comparison || '—')}</div>
    </div>
  </div>
  ${vc.dominant_path_stable === false ? `
  <div style="margin-bottom:10px;padding:10px 12px;background:#FFF0B3;border:1px solid #FFE380;border-radius:8px;font-size:13px;color:#974F0C;">
    <strong>Dominant path changed</strong> — the most common workflow route shifted between periods.
    <div style="margin-top:6px;font-size:12px;"><strong>Baseline:</strong> ${esc(vc.dominant_path_baseline || '—')}</div>
    <div style="font-size:12px;"><strong>Comparison:</strong> ${esc(vc.dominant_path_comparison || '—')}</div>
  </div>` : ''}
  <div style="font-size:13px;line-height:1.65;color:#42526E;">${esc(vc.summary || '')}</div>
</div>`;
};

const buildHypotheses = (data) => {
  const hypotheses = data.hypotheses || [];
  if (hypotheses.length === 0) return '';

  const cards = hypotheses.map((h) => `
    <div class="pc-hypothesis-card">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;margin-bottom:6px;">
        <div class="pc-hypothesis-title">${esc(h.title)}</div>
        ${confidenceBadge(h.confidence || 'Moderate')}
      </div>
      <div class="pc-hypothesis-text">${esc(h.hypothesis)}</div>
      ${h.supporting_evidence ? `<div class="pc-hypothesis-evidence">${esc(h.supporting_evidence)}</div>` : ''}
    </div>`).join('');

  return `
<div class="pc-section">
  <div class="pc-section-header">
    <div class="pc-section-icon" style="background:#6554C0;">💡</div>
    <div class="pc-section-title">What Likely Drove the Changes</div>
  </div>
  ${cards}
</div>`;
};

const buildStableSignals = (data) => {
  const signals = data.stable_signals || [];
  if (signals.length === 0) return '';

  const items = signals.map((s) => `
    <div style="display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #EBECF0;">
      <div style="font-size:18px;font-weight:800;color:#97A0AF;flex-shrink:0;width:24px;text-align:center;">→</div>
      <div style="flex:1;">
        <div style="font-size:14px;font-weight:700;color:#172B4D;margin-bottom:3px;">${esc(s.title)} <span style="font-size:12px;font-weight:400;color:#6B778C;">${esc(s.value)}</span></div>
        <div style="font-size:13px;color:#42526E;line-height:1.55;">${esc(s.interpretation)}</div>
      </div>
    </div>`).join('');

  return `
<div class="pc-section">
  <div class="pc-section-header">
    <div class="pc-section-icon" style="background:#6B778C;">→</div>
    <div class="pc-section-title">What Stayed the Same</div>
  </div>
  <div>${items}</div>
</div>`;
};

// ── Main export ────────────────────────────────────────────────────────────────

/**
 * Render the period comparison AI JSON into a self-contained HTML string.
 *
 * @param {object} json           - Parsed AI response from buildPeriodComparisonPrompt
 * @param {string} modelTitle     - Process model title (e.g. "Incidents - Status")
 * @returns {string}              - Complete HTML document string
 */
export const renderPeriodComparisonHTML = (json, modelTitle = '') => {
  if (!json) return '<p style="padding:20px;color:#6B778C;">No comparison data available.</p>';

  const improvementScheme = {
    bg: '#E3FCEF', border: '#57D9A3', text: '#006644',
    iconBg: '#00875A',
  };
  const deteriorationScheme = {
    bg: '#FFEBE6', border: '#FF8F73', text: '#BF2600',
    iconBg: '#DE350B',
  };

  const sections = [
    buildHero(json),
    buildMetricDeltas(json),
    buildSignalBlock(json.improvement_signals,   'What Improved',   improvementScheme,   '▲'),
    buildSignalBlock(json.deterioration_signals, 'What Deteriorated', deteriorationScheme, '▼'),
    buildTransitionChanges(json),
    buildVariantChanges(json),
    buildHypotheses(json),
    buildStableSignals(json),
  ].filter(Boolean).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="renderer-version" content="v2026-period-comparison-v1"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Trend Analysis${modelTitle ? ` — ${modelTitle}` : ''}</title>
<style>${styles}</style>
</head>
<body>
<div class="pc-wrap">
${sections}
</div>
</body>
</html>`;
};

/**
 * Build the export-ready body HTML (no doctype/head) for inclusion in
 * the combined Consulting Report export.
 */
export const buildPeriodComparisonExportHTML = (json, modelTitle = '') => {
  if (!json) return '';
  
  const improvementScheme = {
    bg: '#E3FCEF', border: '#57D9A3', text: '#006644',
    iconBg: '#00875A',
  };
  const deteriorationScheme = {
    bg: '#FFEBE6', border: '#FF8F73', text: '#BF2600',
    iconBg: '#DE350B',
  };
  
  const sections = [
    buildDocHeader(modelTitle),
    buildHero(json),
    buildMetricDeltas(json),
    buildSignalBlock(json.improvement_signals,   'What Improved',   improvementScheme,   '▲'),
    buildSignalBlock(json.deterioration_signals, 'What Deteriorated', deteriorationScheme, '▼'),
    buildTransitionChanges(json),
    buildVariantChanges(json),
    buildHypotheses(json),
    buildStableSignals(json),
  ].filter(Boolean).join('\n');
  
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="renderer-version" content="v2026-period-comparison-v1"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Trend Analysis${modelTitle ? ` — ${modelTitle}` : ''}</title>
<style>${styles}</style>
</head>
<body>
<div class="pc-wrap">
${sections}
</div>
</body>
</html>`;
};
